import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useLanguage } from "./LanguageContext";
import { API_BASE_URL } from '../config';


const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .auth-root {
    font-family: 'DM Sans', sans-serif;
    --ink: #0f172a;
    --ink-light: #64748b;
    --surface: #f8fafc;
    --accent: #2563eb;
    --accent-dark: #1d4ed8;
    --border: #e2e8f0;
    --white: #ffffff;
    --error: #ef4444;
  }

  .auth-panel {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 480px;
    max-width: 100vw;
    background: var(--white);
    box-shadow: 4px 0 40px rgba(0,0,0,0.12);
    z-index: 200;
    display: flex;
    flex-direction: column;
    transform: translateX(-100%);
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
    overflow-y: auto;
  }

  .auth-panel.visible {
    transform: translateX(0);
  }

  .auth-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    backdrop-filter: blur(2px);
    z-index: 190;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.35s ease;
  }

  .auth-overlay.visible {
    opacity: 1;
    pointer-events: all;
  }

  .auth-panel-inner {
    padding: 48px 44px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .auth-close {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ink-light);
    font-size: 22px;
    line-height: 1;
    padding: 6px;
    border-radius: 50%;
    transition: background 0.2s, color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .auth-close:hover {
    background: var(--surface);
    color: var(--ink);
  }

  @keyframes auth-dot-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(37,99,235,0.55); }
    60%  { box-shadow: 0 0 0 7px rgba(37,99,235,0); }
    100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
  }

  .auth-brand-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    margin-right: 7px;
    vertical-align: middle;
    animation: auth-dot-pulse 1s ease-out infinite;
  }

  .auth-brand {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 18px;
    color: var(--ink);
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 48px;
  }

  .auth-title {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 32px;
    color: var(--ink);
    line-height: 1.15;
    margin: 0 0 6px;
  }

  .auth-subtitle {
    font-size: 14px;
    color: var(--ink-light);
    margin: 0 0 40px;
  }

  .auth-field {
    margin-bottom: 20px;
  }

  .auth-field label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
    margin-bottom: 7px;
    letter-spacing: 0.01em;
  }

  .auth-field input {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: var(--ink);
    background: var(--surface);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .auth-field input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    background: var(--white);
  }

  .auth-field input.error {
    border-color: var(--error);
  }

  .auth-field-hint {
    font-size: 12px;
    color: var(--ink-light);
    margin-top: 5px;
  }

  .auth-error-msg {
    font-size: 13px;
    color: var(--error);
    margin-bottom: 16px;
    padding: 10px 14px;
    background: rgba(239, 68, 68, 0.06);
    border-radius: 7px;
    border: 1px solid rgba(239, 68, 68, 0.2);
  }

  .auth-success-msg {
    font-size: 13px;
    color: #059669;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: rgba(5, 150, 105, 0.06);
    border-radius: 7px;
    border: 1px solid rgba(5, 150, 105, 0.2);
  }

  .auth-btn-primary {
    width: 100%;
    padding: 13px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 8px;
    transition: background 0.2s, transform 0.1s;
    letter-spacing: 0.01em;
  }

  .auth-btn-primary:hover {
    background: var(--accent-dark);
  }

  .auth-btn-primary:active {
    transform: scale(0.99);
  }

  .auth-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 28px 0;
    color: var(--ink-light);
    font-size: 12px;
  }

  .auth-divider::before,
  .auth-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  .auth-switch {
    font-size: 13px;
    color: var(--ink-light);
    text-align: center;
    margin-top: 24px;
  }

  .auth-switch button {
    background: none;
    border: none;
    color: var(--accent);
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .auth-terms {
    font-size: 11.5px;
    color: var(--ink-light);
    text-align: center;
    margin-top: 20px;
    line-height: 1.5;
  }

  .auth-forgot {
    text-align: right;
    margin-top: -12px;
    margin-bottom: 20px;
  }

  .auth-forgot button {
    background: none;
    border: none;
    color: var(--ink-light);
    font-size: 12px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    padding: 0;
    transition: color 0.2s;
  }

  .auth-forgot button:hover {
    color: var(--accent);
  }

  .auth-field-pw-wrap {
    position: relative;
  }

  .auth-field-pw-wrap input {
    padding-right: 42px;
  }

  .auth-pw-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ink-light);
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: color 0.2s;
  }

  .auth-pw-toggle:hover {
    color: var(--ink);
  }

  .auth-name-row {
    display: flex;
    gap: 12px;
  }

  .auth-name-row .auth-field {
    flex: 1;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function LoginForm({ onClose, onSwitchToSignup }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignIn = async () => {
    if (!email || !password) {
      setError(t("auth.errFillAll"));
      return;
    }
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError(error.message);
    } else {
      if (stayLoggedIn) {
        localStorage.setItem("georev_persist", "1");
        sessionStorage.removeItem("georev_active");
      } else {
        sessionStorage.setItem("georev_active", "1");
        localStorage.removeItem("georev_persist");
      }
    }
    setLoading(false);
  };

  return (
    <>
      <div className="auth-brand"><span className="auth-brand-dot"/>GeoRev.ai</div>
      <h1 className="auth-title">{t("auth.loginTitle")}</h1>
      <p className="auth-subtitle">{t("auth.loginSubtitle")}</p>

      {error && <div className="auth-error-msg">{error}</div>}

      <div className="auth-field">
        <label>{t("auth.email")}</label>
        <input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSignIn()}
          className={error && !email ? "error" : ""}
        />
      </div>

      <div className="auth-field">
        <label>{t("auth.password")}</label>
        <div className="auth-field-pw-wrap">
          <input
            type={showPw ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSignIn()}
          />
          <button type="button" className="auth-pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
            <EyeIcon open={showPw} />
          </button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--ink-light)", userSelect: "none" }}>
          <input
            type="checkbox"
            checked={stayLoggedIn}
            onChange={e => setStayLoggedIn(e.target.checked)}
            style={{ cursor: "pointer", accentColor: "var(--accent)", width: 15, height: 15 }}
          />
          {t("auth.stayLoggedIn")}
        </label>
        <div className="auth-forgot" style={{ margin: 0 }}>
          <button>{t("auth.forgotPassword")}</button>
        </div>
      </div>

      <button className="auth-btn-primary" onClick={handleSignIn} disabled={loading}>
        {loading ? t("auth.loginBtnLoading") : t("auth.loginBtn")}
      </button>

      {/* Activate when public signup is ready:
      <div className="auth-switch">
        {t("auth.noAccount")}{" "}
        <button onClick={onSwitchToSignup}>{t("auth.signupLink")}</button>
      </div>
      */}

      <div className="auth-terms">
        {t("auth.terms")}
      </div>
    </>
  );
}

const PLAN_LABELS = { trial: "Trial", starter: "Starter", professional: "Professional", agency: "Scale", pilot: "Pilot" };

function SignupForm({ onClose, onSwitchToLogin, defaultPlan = null }) {
  const { t } = useLanguage();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const handleSignUp = async () => {
    if (!firstName || !email || !password) {
      setError(t("auth.errFillAll"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.errPasswordMatch"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.errPasswordLen"));
      return;
    }
    setLoading(true);
    setError("");

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Query param, not a hash fragment: Supabase appends its own
        // #access_token=... hash onto this URL after redirecting, and a
        // second "#" there produces an unparseable double-hash fragment
        // (breaks both our own "confirmed" check and supabase-js's own
        // automatic session-from-URL detection).
        emailRedirectTo: `${window.location.origin}/?confirmed=1`,
        data: {
          full_name: `${firstName} ${lastName}`.trim(),
          first_name: firstName,
          last_name: lastName,
        }
      }
    });

    if (error) {
      setError(error.message);
    } else {
      // Register user row (plan + credits) and track in HubSpot.
      // Use the user returned directly by signUp() — a separate getUser() call here
      // returns null when email confirmation is pending (no session yet), which was
      // silently dropping userId and making /register reject the request.
      try {
        const res = await fetch(`${API_BASE_URL}/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firstName, lastName, plan: defaultPlan || "trial", userId: data?.user?.id })
        });
        if (!res.ok) console.warn("Register call failed:", res.status, await res.text());
      } catch (err) {
        console.warn("Register tracking failed:", err);
      }

      setSuccess(true);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <>
        <div className="auth-brand"><span className="auth-brand-dot"/>GeoRev.ai</div>
        <h1 className="auth-title">{t("auth.checkEmail")}</h1>
        <p className="auth-subtitle">{t("auth.checkEmailDesc")} <strong>{email}</strong>. {t("auth.checkEmailDesc2")}</p>
        <div className="auth-success-msg">✓ Account created successfully</div>
        <div className="auth-switch">
          {t("auth.alreadyConfirmed")} <button onClick={onSwitchToLogin}>{t("auth.loginLink")}</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="auth-brand"><span className="auth-brand-dot"/>GeoRev.ai</div>
      <h1 className="auth-title">{t("auth.signupTitle")}</h1>
      {defaultPlan && PLAN_LABELS[defaultPlan] && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600, color: "#2563eb", marginBottom: 8 }}>
          ✦ {PLAN_LABELS[defaultPlan]} Plan
        </div>
      )}
      <p className="auth-subtitle">{t("auth.signupSubtitle")}</p>

      {error && <div className="auth-error-msg">{error}</div>}

      <div className="auth-name-row">
        <div className="auth-field">
          <label>{t("auth.firstName")} *</label>
          <input
            type="text"
            placeholder="John"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </div>
        <div className="auth-field">
          <label>{t("auth.lastName")}</label>
          <input
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </div>
      </div>

      <div className="auth-field">
        <label>{t("auth.email")} *</label>
        <input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </div>

      <div className="auth-field">
        <label>{t("auth.password")} *</label>
        <div className="auth-field-pw-wrap">
          <input
            type={showPw ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button type="button" className="auth-pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
            <EyeIcon open={showPw} />
          </button>
        </div>
        <div className="auth-field-hint">{t("auth.passwordHint")}</div>
      </div>

      <div className="auth-field">
        <label>{t("auth.confirmPassword")} *</label>
        <div className="auth-field-pw-wrap">
          <input
            type={showPw2 ? "text" : "password"}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSignUp()}
          />
          <button type="button" className="auth-pw-toggle" onClick={() => setShowPw2(v => !v)} tabIndex={-1}>
            <EyeIcon open={showPw2} />
          </button>
        </div>
      </div>

      <button className="auth-btn-primary" onClick={handleSignUp} disabled={loading}>
        {loading ? t("auth.signupBtnLoading") : t("auth.signupBtn")}
      </button>

      <div className="auth-switch">
        {t("auth.hasAccount")} <button onClick={onSwitchToLogin}>{t("auth.loginLink")}</button>
      </div>

      <div className="auth-terms">
        {t("auth.terms")}
      </div>
    </>
  );
}

export default function Auth({ initialMode = null, defaultPlan = null, onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  // Sync wenn PublicLayout den initialMode von aussen ändert
  useEffect(() => { setMode(initialMode); }, [initialMode]);

  // Handle email confirmation redirect
  useEffect(() => {
    // Query param, not the hash — Supabase appends its own #access_token=...
    // hash after this URL, so our own signal can't live there too (see
    // emailRedirectTo above for why that breaks parsing).
    if (new URLSearchParams(window.location.search).get('confirmed') === '1') {
      setConfirmationMessage('Email erfolgreich bestätigt! Bitte logge dich ein.');
      setMode('login');

      // Clean URL (drop the query param, keep nothing else)
      window.history.replaceState({}, '', '/');

      // Clear message after 5 seconds
      setTimeout(() => setConfirmationMessage(''), 5000);
    }
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN') {
          console.log('User signed in:', session?.user);
          handleClose();
        }
        
        if (event === 'USER_UPDATED') {
          console.log('User email confirmed');
          setMode('login');
          setConfirmationMessage('Email bestätigt! Du kannst dich jetzt einloggen.');
          setTimeout(() => setConfirmationMessage(''), 5000);
        }
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const isVisible = mode !== null;

  const handleClose = () => {
    setMode(null);
    if (onClose) onClose();
  };

  return (
    <>
      <style>{styles}</style>

      {/* Confirmation Toast Message */}
      {confirmationMessage && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          background: '#10b981',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          ✓ {confirmationMessage}
        </div>
      )}

      {/* Overlay */}
      <div
        className={`auth-root auth-overlay ${isVisible ? "visible" : ""}`}
        onClick={handleClose}
      />

      {/* Slide-in Panel */}
      <div className={`auth-root auth-panel ${isVisible ? "visible" : ""}`}>
        <button className="auth-close" onClick={handleClose}>✕</button>
        <div className="auth-panel-inner">
          {mode === "login" && (
            <LoginForm
              onClose={handleClose}
              onSwitchToSignup={() => setMode("signup")}
            />
          )}
          {mode === "signup" && (
            <SignupForm
              onClose={handleClose}
              onSwitchToLogin={() => setMode("login")}
              defaultPlan={defaultPlan}
            />
          )}
        </div>
      </div>
    </>
  );
}
